import { createFileRoute } from "@tanstack/react-router";
import {
  type EnrichmentSearch,
  enrichmentSearchSchema,
  type SetEnrichmentSearch,
} from "#/lib/enrichment-search";
import { isSortKey } from "#/lib/sort";
import { useProducts } from "#/queries/products";
import { Pagination } from "../components/Pagination.js";
import { ProductCard, ProductGridSkeleton } from "../components/ProductCard.js";

// Enrichment classes are one-on-one lesson products that all live under a
// single product category. The set is small, so we fetch it in one request and
// sort/paginate client-side (matches /sale). Lessons only appear once their
// products are published in Medusa admin; until then the page renders empty.
const ENRICHMENT_CATEGORY_HANDLE = "enrichment-class";
const PER_PAGE = 24;
const FETCH_LIMIT = 200;

export const Route = createFileRoute("/enrichment-classes")({
  validateSearch: enrichmentSearchSchema,
  head: () => ({ meta: [{ title: "Enrichment Classes" }] }),
  component: EnrichmentClassesRoute,
});

function EnrichmentClassesRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch: SetEnrichmentSearch = (next) => {
    navigate({
      search: (prev: EnrichmentSearch) => ({ ...prev, ...next }),
      replace: true,
      resetScroll: false,
    });
  };
  return <EnrichmentClassesPage search={search} setSearch={setSearch} />;
}

interface EnrichmentClassesPageProps {
  search: EnrichmentSearch;
  setSearch: SetEnrichmentSearch;
}

function EnrichmentClassesPage({
  search,
  setSearch,
}: EnrichmentClassesPageProps) {
  const { data } = useProducts({
    categorySlug: ENRICHMENT_CATEGORY_HANDLE,
    limit: FETCH_LIMIT,
  });

  const isLoading = data === undefined;
  const sorted = [...(data?.items ?? [])];
  if (search.sort === "price-asc")
    sorted.sort((a, b) => a.metadata.priceAmount - b.metadata.priceAmount);
  if (search.sort === "price-desc")
    sorted.sort((a, b) => b.metadata.priceAmount - a.metadata.priceAmount);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const visible = sorted.slice(
    (search.page - 1) * PER_PAGE,
    search.page * PER_PAGE,
  );

  return (
    <div
      data-screen-label="Enrichment Classes"
      className="pt-12 md:pt-16 pb-24"
    >
      <div className="mx-auto w-full max-w-figma px-6 md:px-10">
        <header className="mb-10 max-w-2xl">
          <h1 className="m-0 mb-3 font-bold font-display text-[40px] text-lp-green-mid leading-none tracking-tight md:text-[56px]">
            Enrichment Classes
          </h1>
          <p className="m-0 font-display text-base text-lp-text md:text-lg">
            One-on-one music and movement lessons — piano, voice, guitar,
            ukulele, taekwondo and more. Choose a class to see its teachers and
            available schedules.
          </p>
        </header>

        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="font-display text-base text-lp-text">
            {isLoading
              ? "Loading…"
              : `${sorted.length} ${sorted.length === 1 ? "class" : "classes"}`}
          </div>
          <select
            value={search.sort}
            onChange={(e) => {
              if (isSortKey(e.target.value))
                setSearch({ sort: e.target.value, page: 1 });
            }}
            className="h-10 cursor-pointer rounded-full border-2 border-lp-green bg-white px-4 font-display text-lp-text text-sm outline-none"
          >
            <option value="default">Default Sorting</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
        </div>

        {isLoading ? (
          <ProductGridSkeleton count={PER_PAGE} />
        ) : visible.length === 0 ? (
          <div className="py-24 text-center font-display text-lp-text-lt">
            Class schedules are being updated — please check back soon.
          </div>
        ) : (
          <ul className="m-0 grid list-none grid-cols-2 md:grid-cols-3 gap-x-6 md:gap-x-12 gap-y-10 md:gap-y-14 p-0">
            {visible.map((p) => (
              <li key={p.id}>
                <ProductCard
                  product={p}
                  brandName={p.metadata.publisher ?? "Learning Plus"}
                />
              </li>
            ))}
          </ul>
        )}

        {!isLoading && visible.length > 0 && (
          <Pagination
            page={search.page}
            total={totalPages}
            onPage={(page) => setSearch({ page })}
          />
        )}
      </div>
    </div>
  );
}
